import React, { useState } from 'react';
import ComingSoonModal from '../common/modals/ComingSoonModal';

// Reusable CRM Systems Section Component
interface CRMSystemsSectionProps {
  crmProviders: Array<{ key: string; name: string; icon: string }>;
  onSelect: (key: string, name: string, type: 'crm') => void;
}

const CRMSystemsSection: React.FC<CRMSystemsSectionProps> = ({ crmProviders, onSelect }) => {
  return (
    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6">
      <h4 className="font-semibold text-gray-900 mb-4">Select your CRM system</h4>
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {crmProviders.map((p) => (
          <button
            key={p.key}
            onClick={() => onSelect(p.key, p.name, 'crm')}
            className="group w-full text-center bg-white border rounded-lg p-3 sm:p-4 shadow-sm hover:shadow-md hover:border-purple-300 transition"
          >
            <div className="flex flex-col sm:flex-row items-center">
              <div className="w-12 h-12 sm:w-10 sm:h-10 rounded-md bg-white flex items-center justify-center mb-2 sm:mb-0 sm:mr-3 border border-gray-200 overflow-hidden">
                <img
                  src={p.icon}
                  alt={p.name}
                  className="w-full h-full object-contain p-1"
                  onError={(e) => {
                    // Fallback to text initials if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) {
                      fallback.style.display = 'flex';
                      fallback.textContent = p.name.split(' ').slice(0, 2).map(w => w[0]).join('');
                    }
                  }}
                />
                <div className="hidden w-full h-full bg-gray-100 text-gray-700 text-xs font-semibold items-center justify-center">
                  {p.name.split(' ').slice(0, 2).map(w => w[0]).join('')}
                </div>
              </div>
              <div className="flex-1 min-w-0 sm:flex sm:items-center sm:flex-1">
                <div className="font-medium text-gray-900 group-hover:text-purple-700 text-xs sm:text-sm truncate">{p.name}</div>
              </div>
              <div className="text-gray-300 group-hover:text-purple-500 text-lg sm:text-xl flex-shrink-0 hidden sm:block">→</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export interface AuthorizeXeroStepProps {
  onboardingStatus: any;
  onAuthorizeXero: () => void;
  loading?: boolean;
  publicMode?: boolean;
}

const AuthorizeXeroStep: React.FC<AuthorizeXeroStepProps> = ({
  onboardingStatus,
  onAuthorizeXero,
  loading = false,
  publicMode = false
}) => {
  const getStepStatus = () => {
    if (!onboardingStatus?.xero_authorization) {
      return 'not_started';
    }
    
    if (onboardingStatus.xero_authorization.is_authorized) {
      return 'authorized';
    }
    
    // Check if authorization was revoked
    if (onboardingStatus.xero_authorization.authorization_status === 'revoked') {
      return 'revoked';
    }
    
    return 'authorizing';
  };

  const status = getStepStatus();

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState<string>('');
  const [selectedProvider, setSelectedProvider] = useState<{ name: string; icon: string } | null>(null);

  const accountingProviders = [
    { key: 'xero', name: 'Xero', icon: '/accounting-systems/Xero-square-thumbnail-144x144.png' },
    { key: 'qbo', name: 'QuickBooks Online', icon: '/accounting-systems/QuickBooks-square_Uf26B71-thumbnail-144x144.png' },
    { key: 'bc', name: 'Microsoft Dynamics 365 Business Central', icon: '/accounting-systems/Microsoft_Dynamics_365_Business_Central_Color_9xEF667-thumbnail-144x144.png' },
    { key: 'd365fo', name: 'Microsoft Dynamics Finance & Operations', icon: '/accounting-systems/PlatformMicrosoft_Dynamics_Finance__Operations-thumbnail-144x144.png' },
    { key: 'netsuite', name: 'NetSuite', icon: '/accounting-systems/Netsuite_square-thumbnail-144x144.png' },
    { key: 'zoho', name: 'Zoho Books', icon: '/accounting-systems/ZohoBooks_Square_Logo-thumbnail-144x144.png' },
    { key: 'sage-business', name: 'Sage Business Accounting', icon: '/accounting-systems/Sage_BCA_Square_KvXv3Sj-thumbnail-144x144.png' },
    { key: 'sage-intacct', name: 'Sage Intacct', icon: '/accounting-systems/Sage_Intacct_Square_vviNs7a-thumbnail-144x144.png' },
    { key: 'wave', name: 'Wave Financial', icon: '/accounting-systems/PlatformWave_Hfdx20t-thumbnail-144x144.png' },
    { key: 'moneybird', name: 'Moneybird', icon: '/accounting-systems/PlatformMoneybird-thumbnail-144x144.png' },
    { key: 'freshbooks', name: 'FreshBooks', icon: '/accounting-systems/FreshBooks_Square_Logo-thumbnail-144x144-70.jpg' },
    { key: 'freeagent', name: 'FreeAgent', icon: '/accounting-systems/Platformfreeagent_square_kRtyDG2-thumbnail-144x144.png' },
    { key: 'clearbooks', name: 'Clear Books', icon: '/accounting-systems/Clear_Books_Square_Logo-thumbnail-144x144-70.jpg' }
  ];

  const crmProviders = [
    { key: 'accelo', name: 'Accelo', icon: '/crm-systems/Platformaccelo_square-thumbnail-144x144.png' },
    { key: 'activecampaign', name: 'ActiveCampaign', icon: '/crm-systems/ActiveCampaign_square-thumbnail-144x144-70.jpg' },
    { key: 'affinity', name: 'Affinity', icon: '/crm-systems/Affinity_square-thumbnail-144x144.png' },
    { key: 'capsule', name: 'Capsule', icon: '/crm-systems/PlatformCapsule-thumbnail-144x144.png' },
    { key: 'close', name: 'Close', icon: '/crm-systems/PlatformClose-thumbnail-144x144-70.jpg' },
    { key: 'copper', name: 'Copper', icon: '/crm-systems/PlatformCopper-thumbnail-144x144-70.jpg' },
    { key: 'hubspot', name: 'HubSpot', icon: '/crm-systems/Hubspot_square-thumbnail-144x144-70.jpg' },
    { key: 'insightly', name: 'Insightly', icon: '/crm-systems/Insightly_square-thumbnail-144x144.png' },
    { key: 'keap', name: 'Keap', icon: '/crm-systems/Platformkeap_square-thumbnail-144x144.png' },
    { key: 'dynamics365-sales', name: 'Microsoft Dynamics 365 Sales', icon: '/crm-systems/PlatformDynamics365Sales_square-thumbnail-144x144.png' },
    { key: 'nutshell', name: 'Nutshell', icon: '/crm-systems/Nutshell-thumbnail-144x144.png' },
    { key: 'pipedrive', name: 'Pipedrive', icon: '/crm-systems/PlatformPipedrive-thumbnail-144x144-70.jpg' },
    { key: 'pipeliner', name: 'Pipeliner', icon: '/crm-systems/Pipeliner_square-thumbnail-144x144.png' },
    { key: 'salesflare', name: 'Salesflare', icon: '/crm-systems/PlatformSalesflare-thumbnail-144x144-70.jpg' },
    { key: 'salesforce', name: 'Salesforce', icon: '/crm-systems/PlatformSalesforce-thumbnail-144x144.png' },
    { key: 'sugarcrm', name: 'SugarCRM', icon: '/crm-systems/Platformsugarcrm_square-thumbnail-144x144.png' },
    { key: 'teamleader', name: 'Teamleader', icon: '/crm-systems/Teamleader-thumbnail-144x144.png' },
    { key: 'teamwork-crm', name: 'Teamwork CRM', icon: '/crm-systems/PlatformTeamwork_CRM_square-thumbnail-144x144-70.jpg' },
    { key: 'vtiger', name: 'Vtiger', icon: '/crm-systems/Platformvtiger_square-thumbnail-144x144.png' },
    { key: 'zendesk-sell', name: 'Zendesk Sell', icon: '/crm-systems/PlatformZendesk_8wt2TTW-thumbnail-144x144.png' },
    { key: 'zoho-crm', name: 'Zoho CRM', icon: '/crm-systems/Platformzohocrm_square-thumbnail-144x144.png' }
  ];

  const handleSelect = (key: string, name: string, type: 'accounting' | 'crm') => {
    if (key === 'xero') {
      onAuthorizeXero();
      return;
    }
    const provider = type === 'accounting' 
      ? accountingProviders.find(p => p.key === key)
      : crmProviders.find(p => p.key === key);
    if (provider) {
      setSelectedProvider({ name: provider.name, icon: provider.icon });
      setModalMessage(`${name} integration is coming soon. Stay tuned!`);
      setShowModal(true);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
      <div className="flex items-center mb-6">
        <div className={`flex items-center justify-center w-12 h-12 rounded-full text-2xl mr-4 ${
          status === 'authorized' ? 'bg-green-100 text-green-600' :
          status === 'authorizing' ? 'bg-yellow-100 text-yellow-600' :
          'bg-blue-100 text-blue-600'
        }`}>
          🔗
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">Step 1: Connect your accounting system & CRM system</h3>
          <p className="text-gray-600">
            {status === 'authorized' ? 'Connected successfully' :
             status === 'authorizing' ? 'Authorization in progress' :
             'Choose your accounting system to continue'}
          </p>
        </div>
      </div>

      {status === 'not_started' && (
        <div className="space-y-6">
          {/* Provider selection grid */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Select your accounting system</h4>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {accountingProviders.map((p) => (
                <button
                  key={p.key}
                  disabled={loading}
                  onClick={() => handleSelect(p.key, p.name, 'accounting')}
                  className={`group w-full text-center bg-white border rounded-lg p-3 sm:p-4 shadow-sm hover:shadow-md transition ${
                    loading ? 'cursor-not-allowed opacity-70' : 'hover:border-blue-300'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row items-center">
                    <div className="w-12 h-12 sm:w-10 sm:h-10 rounded-md bg-white flex items-center justify-center mb-2 sm:mb-0 sm:mr-3 border border-gray-200 overflow-hidden">
                      <img
                        src={p.icon}
                        alt={p.name}
                        className="w-full h-full object-contain p-1"
                        onError={(e) => {
                          // Fallback to text initials if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) {
                            fallback.style.display = 'flex';
                            fallback.textContent = p.name.split(' ').slice(0, 2).map(w => w[0]).join('');
                          }
                        }}
                      />
                      <div className="hidden w-full h-full bg-gray-100 text-gray-700 text-xs font-semibold items-center justify-center">
                        {p.name.split(' ').slice(0, 2).map(w => w[0]).join('')}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 sm:flex sm:items-center sm:flex-1">
                      <div className="font-medium text-gray-900 group-hover:text-blue-700 text-xs sm:text-sm truncate">{p.name}</div>
                      <div className="text-xs text-gray-500 hidden sm:block">
                        {p.key === 'xero' ? 'OAuth connection' : ''}
                      </div>
                    </div>
                    <div className="text-gray-300 group-hover:text-blue-500 text-lg sm:text-xl flex-shrink-0 hidden sm:block">→</div>
                  </div>
                </button>
              ))}
            </div>
            {loading && (
              <p className="text-sm text-gray-600 mt-3">Setting up Xero integration...</p>
            )}
          </div>

          {/* CRM Systems section */}
          <CRMSystemsSection crmProviders={crmProviders} onSelect={handleSelect} />
        </div>
      )}

      {status === 'authorizing' && (
        <div className="space-y-6">
          {/* Authorization in progress */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-600 mr-3"></div>
              <h4 className="text-lg font-semibold text-yellow-800">Authorization in Progress</h4>
            </div>
            <p className="text-yellow-700 mb-4">
              We're waiting for you to complete the Xero authorization process.
            </p>
            
            {onboardingStatus.xero_authorization.authorized_at && (
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="text-yellow-600">Authorization started: </span>
                  <span className="text-yellow-800">
                    {new Date(onboardingStatus.xero_authorization.authorized_at).toLocaleString()}
                  </span>
                </div>
                {onboardingStatus.xero_authorization.expired_at && (
                  <div className="text-sm">
                    <span className="text-yellow-600">Link expires: </span>
                    <span className="text-yellow-800">
                      {new Date(onboardingStatus.xero_authorization.expired_at).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 rounded-lg p-6">
            <h4 className="font-semibold text-blue-900 mb-3">Next Steps:</h4>
            <ol className="list-decimal list-inside space-y-2 text-blue-800">
              <li>Check your email for the Xero authorization link</li>
              <li>Click the link to open Xero in a new window</li>
              <li>Log in to your Xero account and grant permissions</li>
              <li>Return to this page to see the connection status</li>
            </ol>
          </div>

          {/* CRM Systems section */}
          <CRMSystemsSection crmProviders={crmProviders} onSelect={handleSelect} />
        </div>
      )}

      {status === 'authorized' && (
        <div className="space-y-6">
          {/* Success state */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <div className="text-green-600 text-2xl mr-3">✅</div>
              <h4 className="text-lg font-semibold text-green-800">Xero Successfully Connected!</h4>
            </div>
            <p className="text-green-700 mb-2">
              Your Xero account is now connected and we're analyzing your data to provide intelligent insights.
            </p>
            
            {onboardingStatus.xero_authorization.authorized_at && (
              <div className="text-sm">
                <span className="text-green-600">Connected on: </span>
                <span className="text-green-800">
                  {new Date(onboardingStatus.xero_authorization.authorized_at).toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {/* What happens next */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-4">What happens next?</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <div className="text-2xl mr-3">📊</div>
                <div>
                  <h5 className="font-medium text-gray-900">Data Analysis</h5>
                  <p className="text-sm text-gray-600">We're analyzing your customer data and payment patterns</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-2xl mr-3">🎯</div>
                <div>
                  <h5 className="font-medium text-gray-900">Customer Insights</h5>
                  <p className="text-sm text-gray-600">Generating personalized insights for each customer</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-2xl mr-3">⚡</div>
                <div>
                  <h5 className="font-medium text-gray-900">Smart Recommendations</h5>
                  <p className="text-sm text-gray-600">Creating optimized collection strategies</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-2xl mr-3">📈</div>
                <div>
                  <h5 className="font-medium text-gray-900">Performance Tracking</h5>
                  <p className="text-sm text-gray-600">Monitoring and improving your collection rates</p>
                </div>
              </div>
            </div>
          </div>

          {/* CRM Systems section */}
          <CRMSystemsSection crmProviders={crmProviders} onSelect={handleSelect} />
        </div>
      )}

      {status === 'revoked' && (
        <div className="space-y-6">
          {/* Revoked state */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <div className="text-red-600 text-2xl mr-3">⚠️</div>
              <h4 className="text-lg font-semibold text-red-800">Xero Authorization Revoked</h4>
            </div>
            <p className="text-red-700 mb-4">
              Your Xero authorization has been revoked. You'll need to re-authorize to continue using ARiA's features.
            </p>
            
            {onboardingStatus.xero_authorization.authorized_at && (
              <div className="text-sm">
                <span className="text-red-600">Previously connected on: </span>
                <span className="text-red-800">
                  {new Date(onboardingStatus.xero_authorization.authorized_at).toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {/* Re-authorization CTA */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-3">Re-authorize Xero Connection</h4>
            <p className="text-gray-700 mb-4">
              Click the button below to re-establish your Xero connection and restore access to all ARiA features.
            </p>
            
            <div className="text-center">
              <button
                onClick={onAuthorizeXero}
                disabled={loading}
                className={`px-8 py-4 rounded-lg font-semibold text-lg transform transition-all duration-200 shadow-lg ${
                  loading
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:scale-105'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Re-authorizing Xero...
                  </div>
                ) : (
                  '🔄 Re-authorize Xero Connection'
                )}
              </button>
              <p className="text-sm text-gray-500 mt-2">
                {loading ? 'Please wait while we set up your integration...' : 'Secure OAuth connection • Takes less than 2 minutes'}
              </p>
            </div>
          </div>

          {/* What you'll regain */}
          <div className="bg-blue-50 rounded-lg p-6">
            <h4 className="font-semibold text-blue-900 mb-3">What you'll regain after re-authorization:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <div className="text-2xl mr-3">📊</div>
                <div>
                  <h5 className="font-medium text-blue-900">Data Access</h5>
                  <p className="text-sm text-blue-700">Access to your customer and invoice data</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-2xl mr-3">🎯</div>
                <div>
                  <h5 className="font-medium text-blue-900">Customer Analysis</h5>
                  <p className="text-sm text-blue-700">AI-powered insights and recommendations</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-2xl mr-3">⚡</div>
                <div>
                  <h5 className="font-medium text-blue-900">Automated Collections</h5>
                  <p className="text-sm text-blue-700">Smart reminder sequences and follow-ups</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="text-2xl mr-3">📈</div>
                <div>
                  <h5 className="font-medium text-blue-900">Performance Tracking</h5>
                  <p className="text-sm text-blue-700">Monitor and improve collection rates</p>
                </div>
              </div>
            </div>
          </div>

          {/* CRM Systems section */}
          <CRMSystemsSection crmProviders={crmProviders} onSelect={handleSelect} />
        </div>
      )}

      {/* Coming Soon Modal */}
      <ComingSoonModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={`Integration for ${selectedProvider?.name || 'this system'} is coming soon`}
        message={`We're working hard to bring you seamless integration with ${selectedProvider?.name || 'this system'}. Stay tuned for updates!`}
        icon="🚀"
      />
    </div>
  );
};

export default AuthorizeXeroStep;
